import {
  AuthenticationResult,
  EventType,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { json, redirect, useSearchParams } from 'react-router-dom';
import { useProfile } from './api';
import { ApiProfile, UserRole } from './types';

/* eslint-disable no-console */
export const loggerCallback = (loglevel: LogLevel, message: string) => {
  switch (loglevel) {
    case LogLevel.Error:
      console.error(message);
      break;
    case LogLevel.Warning:
      console.warn(message);
      break;
    case LogLevel.Info:
      console.info(message);
      break;
    case LogLevel.Trace:
      console.debug(message);
      break;
    case LogLevel.Verbose:
      console.log(message);
      break;
  }
};

const { VITE_CLIENT_ID, VITE_TENANT } = import.meta.env;

const msalConfig = {
  auth: {
    clientId: VITE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${VITE_TENANT}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback,
      piiLoggingEnabled: false,
      logLevel: LogLevel.Warning,
    },
  },
};
export const loginRequest = {
  scopes: [`${VITE_CLIENT_ID}/.default`],
};

export const msalClient = new PublicClientApplication(msalConfig);

export const initAuthentication = async () => {
  const res = await msalClient.handleRedirectPromise();
  if (res !== null) {
    msalClient.setActiveAccount(res.account);
    if (res.state) {
      const state = JSON.parse(res.state);

      window.location.assign(state.returnUrl);
    }
  } else {
    const accounts = msalClient.getAllAccounts();
    if (accounts?.length) {
      msalClient.setActiveAccount(accounts[0]);
    }
  }

  msalClient.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const account = (event.payload as AuthenticationResult).account;
      msalClient.setActiveAccount(account);
    }
  });
};

/**
 * @returns true when the use has at least one of the provided roles
 */
export const hasRole = (roles: string[], profileRoles: string[]) =>
  roles.length === 0 || roles?.some(role => profileRoles.includes(role));

// in the context of the portal a user can either be in the student or instructor role so we filter out the student role if the user is an instructor
export const filterRoles = (roles: string[]) =>
  roles.includes(UserRole.Instructor) && !import.meta?.env?.DEV
    ? roles.filter(role => role !== UserRole.Student)
    : roles;

export interface LoaderOptions {
  roles?: string[];
}

export type AuthLoaderData =
  | undefined
  | {
      profilePromise: Promise<ApiProfile>;
    };

// Loader for routes that need authentication
export const authLoader = ({ request }): Response | null => {
  const account = msalClient.getActiveAccount();

  // In Cypress we have a token on localStorage instead of an active account on MSAL instance
  if (window.Cypress ? !window.localStorage.getItem('cypressAccessToken') : !account) {
    return redirect(`/login?returnUrl=${request.url.replace(window.location.origin, '')}`);
  }

  return null;
};

export const nonAuthLoader = () => {
  const account = msalClient.getActiveAccount();

  if (window.Cypress ? window.localStorage.getItem('cypressAccessToken') : account) {
    return redirect('/');
  }

  return null;
};

export const getToken = async () => {
  try {
    const { accessToken } = window.Cypress
      ? { accessToken: window.localStorage.getItem('cypressAccessToken') }
      : await msalClient.acquireTokenSilent(loginRequest);

    return accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalClient.acquireTokenRedirect(loginRequest);
    }
  }
};

export const useIsAuthenthicated = () => {
  return !!window.Cypress || useIsAuthenticated();
};

export const useAuth = () => {
  const msal = useMsal();
  const [params] = useSearchParams();

  const loginRedirect = () => {
    msal.instance.loginRedirect({
      ...loginRequest,
      state: JSON.stringify({ returnUrl: params.get('returnUrl') ?? window.location.pathname }),
    });
  };

  const logoutRedirect = () => {
    msal.instance.logoutRedirect();
  };

  return { logoutRedirect, loginRedirect };
};

export const useCheckRole = (roles: string[]) => {
  const { profile } = useProfile();

  useEffect(() => {
    if (!profile) {
      return;
    }
    const profileRoles = profile.companies[0]?.roles ?? [];
    if (roles && !hasRole(roles, profileRoles)) {
      throw json(
        { message: 'You do not have permission to view this page' },
        { status: 403, statusText: 'No permission' },
      );
    }
  }, [profile]);
};

export const userHasOnlyStudentRole = () => {
  const { profile } = useProfile();
  if (!profile) {
    return;
  }

  return profile.companies[0]?.roles?.length === 1 && profile.companies[0]?.roles[0] === UserRole.Student;
};

export const hasRouteAccess = () => !!useProfile().profile?.showRoutesMenu;

type GroupSimulationCompany = {
  isHomeCompany?: boolean;
  settings?: {
    canViewGroupSimulations?: boolean;
  };
};

export const profileCanViewGroupSimulations = (companies: GroupSimulationCompany[] = []) =>
  !!companies.find(company => company.isHomeCompany)?.settings?.canViewGroupSimulations;

export const hasGroupSimulationAccess = () => profileCanViewGroupSimulations(useProfile().profile?.companies);
